import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, InputGroup, Tag, HTMLSelect, Spinner, NonIdealState, Callout, Icon } from "@blueprintjs/core";
import { api, type Lead, type LeadListResponse } from "../lib/api";

interface CompanyGroup {
  id: string | null;
  name: string;
  domain: string | null;
  leads: Lead[];
}

function groupLeadsByCompany(leads: Lead[]): CompanyGroup[] {
  const groups = new Map<string, CompanyGroup>();
  for (const l of leads) {
    const key = l.company_id || l.company_name || "__unassigned__";
    if (!groups.has(key)) {
      groups.set(key, {
        id: l.company_id || null,
        name: l.company_name || "Unknown Company",
        domain: l.company_domain || null,
        leads: [],
      });
    }
    groups.get(key)!.leads.push(l);
  }
  return Array.from(groups.values()).sort((a, b) => b.leads.length - a.leads.length);
}

const AVATAR_COLORS = ["#2b95d6", "#634dbf", "#0d8050", "#c87619", "#8f398f", "#1f6b6b", "#96290d", "#5642a6"];
function avatarColor(name: string) { let h = 0; for (const c of name) h = ((h << 5) - h + c.charCodeAt(0)) | 0; return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]; }

function CompanyRow({ group }: { group: CompanyGroup }) {
  const navigate = useNavigate();
  const { leads } = group;
  const bestScore = Math.max(...leads.map(l => l.score));
  const claimedCount = leads.filter(l => l.status !== "unclaimed").length;
  const statusSummary = (() => {
    if (claimedCount === 0) return null;
    const won = leads.filter(l => l.status === "won").length;
    const qual = leads.filter(l => l.status === "qualified").length;
    const dead = leads.filter(l => l.status === "dead").length;
    const parts: string[] = [];
    if (won > 0) parts.push(`${won} won`);
    if (qual > 0) parts.push(`${qual} qualified`);
    if (dead > 0) parts.push(`${dead} dead`);
    if (parts.length === 0) parts.push(`${claimedCount} claimed`);
    return parts.join(", ");
  })();

  const handleClick = () => {
    if (group.id) {
      navigate(`/companies/${group.id}`);
    }
  };

  return (
    <tr
      className="company-row clickable"
      onClick={handleClick}
      style={{ cursor: group.id ? "pointer" : "default" }}
    >
      <td style={{ width: "30%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: avatarColor(group.name), display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#fff",
          }}>
            {group.name.charAt(0).toUpperCase()}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, lineHeight: 1.3 }}>{group.name}</div>
            {group.domain && <div style={{ fontSize: 11, color: "#5c7080", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.domain}</div>}
          </div>
        </div>
      </td>
      <td style={{ width: "15%" }}>
        <Tag minimal round style={{ fontSize: 11.5 }}>{leads.length} {leads.length === 1 ? "contact" : "contacts"}</Tag>
      </td>
      <td style={{ width: "20%" }}>
        {statusSummary && <span style={{ fontSize: 11.5, color: "#5c7080" }}>{statusSummary}</span>}
      </td>
      <td style={{ width: "10%" }}>
        <Tag minimal round intent={bestScore >= 80 ? "success" : bestScore >= 60 ? "warning" : "none"} style={{ fontWeight: 700 }}>
          {bestScore > 0 ? bestScore : "—"}
        </Tag>
      </td>
      <td style={{ width: "13%" }}>
        {claimedCount > 0 ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: "#e1e8ed", overflow: "hidden" }}>
              <div style={{ width: `${(claimedCount / leads.length) * 100}%`, height: "100%", background: "#2b95d6", borderRadius: 2 }} />
            </div>
            <span style={{ fontSize: 11, color: "#5c7080" }}>{claimedCount}/{leads.length}</span>
          </div>
        ) : (
          <Tag minimal round style={{ fontSize: 11 }}>New</Tag>
        )}
      </td>
      <td style={{ width: "12%", textAlign: "right" }}>
        {group.id && <Icon icon="chevron-right" size={14} color="#8a9ba8" />}
      </td>
    </tr>
  );
}

export function Leads() {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);

  const fetchLeads = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", "100");
    if (statusFilter) params.set("status", statusFilter);
    if (search) params.set("search", search);

    api.get<LeadListResponse>(`/leads/?${params}`)
      .then((r) => { setLeads(r.leads); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [page, statusFilter, search]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchLeads();
  };

  const companyGroups = groupLeadsByCompany(leads);

  return (
    <div>
      <div className="dash-header">
        <div>
          <h2>Companies</h2>
          <p>All discovered companies across your blueprints</p>
        </div>
      </div>

      {error && <Callout intent="danger" style={{ marginBottom: 16 }}>{error}</Callout>}

      <div className="dash-panel">
        <div className="dash-panel-header">
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <form onSubmit={handleSearch} style={{ display: "inline" }}>
              <InputGroup
                leftIcon="search"
                placeholder="Search companies..."
                style={{ width: 280 }}
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </form>
            <HTMLSelect
              minimal
              style={{ fontSize: 13 }}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">All Statuses</option>
              <option value="unclaimed">Unclaimed</option>
              <option value="claimed">Claimed</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="won">Won</option>
              <option value="dead">Dead</option>
            </HTMLSelect>
          </div>
          <span style={{ fontSize: 12, color: "#5c7080" }}>
            {companyGroups.length} {companyGroups.length === 1 ? "company" : "companies"} · {total} contacts
          </span>
        </div>
        <div className="dash-panel-body no-pad">
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
              <Spinner size={28} />
            </div>
          ) : companyGroups.length === 0 ? (
            <div style={{ padding: 40 }}>
              <NonIdealState
                icon="office"
                title="No companies found"
                description="Companies will appear here once blueprints generate them."
              />
            </div>
          ) : (
            <table className="lead-table" style={{ tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "30%" }}>Company</th>
                  <th style={{ width: "15%" }}>Contacts</th>
                  <th style={{ width: "20%" }}>Summary</th>
                  <th style={{ width: "10%" }}>Score</th>
                  <th style={{ width: "13%" }}>Progress</th>
                  <th style={{ width: "12%" }}></th>
                </tr>
              </thead>
              <tbody>
                {companyGroups.map(g => (
                  <CompanyRow key={g.id || g.name} group={g} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        {total > 100 && (
          <div style={{ padding: "12px 20px", borderTop: "1px solid #dce0e5", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button disabled={page <= 1} icon="chevron-left" onClick={() => setPage(page - 1)} size="small">Previous</Button>
            <span style={{ fontSize: 12, color: "#5c7080" }}>Page {page} of {Math.ceil(total / 100)}</span>
            <Button disabled={page * 100 >= total} rightIcon="chevron-right" onClick={() => setPage(page + 1)} size="small">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
